import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { Box, Typography, Avatar, TextField, Button, Drawer, Fab, Chip, IconButton, useMediaQuery } from '@mui/material'
import { Person as PersonIcon, Send as SendIcon, Stop as StopIcon, KeyboardArrowDown as KeyboardArrowDownIcon, Close as CloseIcon, DeleteOutline as DeleteOutlineIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material'
import BusinessIcon from '@mui/icons-material/Business'
import { useTheme } from '@mui/material/styles'
import { checkEmpty, imageAddress } from 'data/helpers'
import { sendChatMessage, addChatMessage, setMobileDrawerState, setActiveJobContext, stopChatRequest, clearConversation } from '/src/redux/aiJobSearch'
import { buildActiveFiltersSummary } from './filterSyncHelpers'
import TypingIndicator from './TypingIndicator'
import MessageFeedback from './MessageFeedback'
import MyStethiAIIcon from '/src/components/Icons/MyStethiAIIcon'
import { transitions, springTransition } from './animations'
import { generateExampleQueries, generateFollowUpSuggestions, buildWelcomeMessage, buildTypingIndicatorMessages, buildJobContextPrefix } from './helpers'

const AIChatBot = ({ inline = false, collapsed = false, onCollapse }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [prompt, setPrompt] = useState('')
  const [drawerOpen, setDrawerOpen] = useState(true)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  // Resizable state
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const key = inline ? 'aiSidebarWidth' : 'aiChatDrawerWidth'
    const saved = localStorage.getItem(key)
    return saved ? parseInt(saved) : (inline ? 400 : 450)
  })
  const [isResizing, setIsResizing] = useState(false)

  // Get user from state
  const candidate = useSelector(state => state.candidate)
  const profile = useSelector(state => state.profile?.profile)
  const { conversationId, chatHistory, isAiTyping, rankedJobs, jobListingArguments, mobileDrawerState, activeJobContext, suggestedFollowUps, typingStatus } = useSelector(state => state.aiJobSearch)
  const [searchParams] = useSearchParams()
  const filterValues = useSelector(state => state.filters.jobFilterValues) || {}

  const candidateSpecialtyLabels = useMemo(() => {
    const labels = {}
    candidate?.specialties?.forEach(s => { labels[String(s.id)] = s.name })
    return labels
  }, [candidate?.specialties])

  const exampleQueries = useMemo(
    () => generateExampleQueries(candidate),
    [candidate]
  )

  const lastUserMessage = chatHistory.filter(m => m.type === 'user').pop()?.message
  const typingMessages = useMemo(
    () => buildTypingIndicatorMessages({ candidate, jobListingArguments, lastUserMessage }),
    [candidate, jobListingArguments, lastUserMessage]
  )

  const followUpSuggestions = useMemo(
    () => suggestedFollowUps?.length > 0
      ? suggestedFollowUps
      : generateFollowUpSuggestions({ rankedJobs, jobListingArguments }),
    [suggestedFollowUps, rankedJobs, jobListingArguments]
  )

  const profileImageObj = profile?.profileImage || candidate?.profileImage
  const profileImage = profileImageObj && !checkEmpty(profileImageObj.key)
    ? imageAddress(profileImageObj.key, profileImageObj.fileName)
    : null

  // Add welcome message on first load (wait for candidate data to be fetched)
  const candidateReady = !candidate.loading && !!candidate.id
  useEffect(() => {
    if (chatHistory.length === 0 && candidateReady) {
      dispatch(addChatMessage({
        type: 'ai',
        message: buildWelcomeMessage(candidate),
        metadata: { isWelcome: true }
      }))
    }
  }, [dispatch, chatHistory.length, candidateReady])

  // Scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatHistory, isAiTyping])

  // Clear conversation on unmount
  useEffect(() => {
    return () => {
      dispatch(clearConversation())
    }
  }, [dispatch])

  // Auto-minimize drawer on mobile when jobs arrive
  useEffect(() => {
    if (isMobile && rankedJobs.length > 0 && mobileDrawerState === 'full') {
      dispatch(setMobileDrawerState('minimized'))
    }
  }, [isMobile, rankedJobs.length, mobileDrawerState, dispatch])

  // Determine drawer open state for mobile
  const isDrawerOpen = isMobile
    ? (mobileDrawerState === 'full' || mobileDrawerState === 'expanded')
    : drawerOpen

  // Hide FAB on mobile when drawer is minimized (bottom bar shows instead)
  const showFab = isMobile
    ? (mobileDrawerState === 'hidden')
    : !inline

  const toggleDrawer = () => {
    if (inline && onCollapse) {
      onCollapse()
      return
    }
    if (isMobile) {
      if (mobileDrawerState === 'full' || mobileDrawerState === 'expanded') {
        dispatch(setMobileDrawerState('minimized'))
      } else if (mobileDrawerState === 'minimized') {
        dispatch(setMobileDrawerState('expanded'))
      } else {
        dispatch(setMobileDrawerState('full'))
      }
    } else {
      setDrawerOpen(!drawerOpen)
    }
  }

  // Resize handlers
  const startResize = (e) => {
    if (isMobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        if (inline) {
          const container = e.target.closest?.('[data-ai-sidebar-parent]') || document.body
          const containerRect = container.getBoundingClientRect?.() || { right: window.innerWidth }
          const maxWidth = containerRect.width * 0.5
          const newWidth = containerRect.right - e.clientX
          if (newWidth >= 300 && newWidth <= maxWidth) {
            setSidebarWidth(newWidth)
          }
        } else {
          const newWidth = window.innerWidth - e.clientX
          if (newWidth >= 350) {
            setSidebarWidth(newWidth)
          }
        }
      })
    }

    const stopResize = () => {
      setIsResizing(false)
      const key = inline ? 'aiSidebarWidth' : 'aiChatDrawerWidth'
      localStorage.setItem(key, sidebarWidth.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, sidebarWidth, inline])

  // Body CSS injection — only for drawer mode (not inline)
  useEffect(() => {
    if (inline) return

    const styleId = 'ai-drawer-styles'
    let styleElement = document.getElementById(styleId)

    if (!styleElement) {
      styleElement = document.createElement('style')
      styleElement.id = styleId
      document.head.appendChild(styleElement)
    }

    if (isDrawerOpen && !isMobile) {
      document.body.classList.add('ai-drawer-open')
      document.documentElement.classList.add('ai-drawer-open')

      styleElement.textContent = `
        body.ai-drawer-open main,
        body.ai-drawer-open #root,
        body.ai-drawer-open [data-testid="main-content"] {
          margin-right: ${sidebarWidth}px !important;
          transition: margin-right 0.3s ease !important;
        }

        body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
          margin-right: ${sidebarWidth}px !important;
          transition: margin-right 0.3s ease !important;
          max-width: none !important;
        }

        @media (max-width: 959px) {
          body.ai-drawer-open main,
          body.ai-drawer-open #root,
          body.ai-drawer-open [data-testid="main-content"],
          body.ai-drawer-open .MuiContainer-root:not(.ai-drawer-container) {
            margin-right: 0 !important;
          }
        }
      `
    } else {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      styleElement.textContent = ''
    }

    return () => {
      document.body.classList.remove('ai-drawer-open')
      document.documentElement.classList.remove('ai-drawer-open')
      const style = document.getElementById(styleId)
      if (style) {
        style.remove()
      }
    }
  }, [isDrawerOpen, sidebarWidth, isMobile, inline])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!prompt.trim()) return
    if (isAiTyping) return

    const filterSummary = !conversationId ? buildActiveFiltersSummary(searchParams, filterValues, candidateSpecialtyLabels) : null

    dispatch(addChatMessage({
      type: 'user',
      message: prompt,
      metadata: activeJobContext ? {
        jobContext: {
          title: activeJobContext.title,
          employer: activeJobContext.employer,
          city: activeJobContext.city,
          state: activeJobContext.state,
        }
      } : undefined,
    }))

    const jobContextPrefix = activeJobContext && !conversationId ? buildJobContextPrefix(activeJobContext) : ''
    let apiMessage = prompt
    if (filterSummary) apiMessage = `[Active filters: ${filterSummary}] ${apiMessage}`
    if (jobContextPrefix) apiMessage = `${jobContextPrefix}\n\n${apiMessage}`
    dispatch(sendChatMessage({
      message: apiMessage,
      conversationId,
    }))

    setPrompt('')
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const handleExampleClick = (query) => {
    if (isAiTyping) return
    const filterSummary = !conversationId ? buildActiveFiltersSummary(searchParams, filterValues, candidateSpecialtyLabels) : null
    dispatch(addChatMessage({ type: 'user', message: query }))
    const apiMessage = filterSummary ? `[Active filters: ${filterSummary}] ${query}` : query
    dispatch(sendChatMessage({ message: apiMessage, conversationId }))
  }

  const handleStop = () => {
    dispatch(stopChatRequest())
  }

  const handleClear = () => {
    dispatch(clearConversation())
  }

  const handleDismissJobContext = () => {
    dispatch(setActiveJobContext(null))
  }

  const hasUserMessage = chatHistory.some(m => m.type === 'user')

  // Render message
  const renderMessage = (message) => {
    // System messages
    if (message.type === 'system' || message.isSystem) {
      return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            my: 0.25,
            px: 2,
          }}
        >
          <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.grey[300] }} />
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.disabled,
              fontSize: '0.7rem',
              whiteSpace: 'nowrap',
            }}
          >
            {message.message}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: theme.palette.grey[300] }} />
        </Box>
      )
    }

    // Regular messages
    return (
      <Box
        key={message.id}
        sx={{
          display: 'flex',
          gap: { xs: 1, md: 1.5 },
          alignItems: 'flex-start',
          flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
        }}
      >
        {message.type === 'user' ? (
          <Avatar
            src={profileImage}
            sx={{
              bgcolor: theme.palette.primary.main,
              width: 32,
              height: 32,
            }}
          >
            <PersonIcon sx={{ fontSize: '1.1rem' }} />
          </Avatar>
        ) : (
          <Avatar
            sx={{
              backgroundColor: theme.palette.ai.main,
              width: 32,
              height: 32,
            }}
          >
            <MyStethiAIIcon
              sx={{
                color: theme.palette.common.white,
                fontSize: '1.1rem',
              }}
            />
          </Avatar>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', maxWidth: '85%' }}>
          {message.jobContext && (
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: theme.palette.ai.dark,
                mb: 0.5,
                px: 0.5,
                justifyContent: 'flex-end',
              }}
            >
              <BusinessIcon sx={{ fontSize: '0.8rem', color: theme.palette.ai.main }} />
              {[
                message.jobContext.title,
                message.jobContext.employer,
                [message.jobContext.city, message.jobContext.state].filter(Boolean).join(', '),
              ].filter(Boolean).join(' \u00B7 ')}
            </Typography>
          )}
          <Box
            sx={{
              backgroundColor: message.isError
                ? theme.palette.error.light
                : (message.type === 'user'
                  ? 'rgba(42, 181, 116, 0.15)'
                  : (message.metadata?.isWelcome ? `${theme.palette.ai.light}40` : theme.palette.grey[100])),
              borderRadius: 2,
              p: 1.5,
              border: message.isError
                ? `1px solid ${theme.palette.error.main}`
                : 'none',
              borderLeft: message.metadata?.isWelcome
                ? `3px solid ${theme.palette.ai.main}`
                : 'none',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                whiteSpace: 'pre-wrap',
                color: message.isError ? theme.palette.error.dark : 'inherit'
              }}
            >
              {message.message}
            </Typography>
            {message.jobsCount > 0 && (
              <Typography variant='caption' sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                Found {message.jobsCount} job{message.jobsCount !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>
          {message.type === 'ai' && !message.isWelcome && !message.isError && (
            <MessageFeedback messageId={message.id} chatbotMessageId={message.chatbotMessageId} feedback={message.feedback} conversationId={conversationId} />
          )}
        </Box>
      </Box>
    )
  }

  // Shared chat content used by both inline and drawer modes
  const chatContent = (
    <Box
      sx={{
        height: inline ? '100%' : (isMobile ? (mobileDrawerState === 'expanded' ? '70vh' : '100vh') : '100vh'),
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        pt: !inline && isMobile && mobileDrawerState === 'full' ? 'calc(56px + env(safe-area-inset-top))' : 0,
      }}
    >
      {/* Mobile Pull Indicator */}
      {!inline && isMobile && (
        <Box
          role="button"
          tabIndex={0}
          aria-label="minimize drawer"
          onClick={() => dispatch(setMobileDrawerState('minimized'))}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dispatch(setMobileDrawerState('minimized')) } }}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 1.5,
            cursor: 'pointer',
            '&:active': {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[300],
            }}
          />
        </Box>
      )}

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          borderTop: !inline && !isMobile ? `3px solid ${theme.palette.ai.main}` : 'none',
          borderBottom: 1,
          borderColor: theme.palette.grey[200],
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.ai.dark }}>
          <MyStethiAIIcon sx={{ color: theme.palette.ai.main, fontSize: '1.2rem' }} />
          Stethi
          <Chip
            label="Beta"
            size="small"
            sx={{
              height: 20,
              fontSize: '0.65rem',
              fontWeight: 700,
              backgroundColor: theme.palette.ai.main,
              color: theme.palette.common.white,
            }}
          />
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {hasUserMessage && (
            <IconButton
              onClick={handleClear}
              size="small"
              title="Clear conversation"
              sx={{ color: theme.palette.grey[600] }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
          <Button
            onClick={toggleDrawer}
            size="small"
            startIcon={<KeyboardArrowDownIcon />}
            sx={{
              color: theme.palette.grey[600],
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              }
            }}
          >
            {isMobile ? 'Minimize' : 'Collapse'}
          </Button>
        </Box>
      </Box>

      {/* Messages Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {chatHistory.map(renderMessage)}
        {isAiTyping && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <TypingIndicator messages={typingMessages} progressStatus={typingStatus} />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Suggestion Pills */}
      {!isAiTyping && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {!hasUserMessage && (
            <Typography variant='caption' sx={{ color: theme.palette.grey[500], width: '100%', mb: 0.25 }}>
              Try:
            </Typography>
          )}
          {(!hasUserMessage ? exampleQueries : followUpSuggestions).map((query) => (
            <Chip
              key={query}
              label={query}
              size='small'
              onClick={() => handleExampleClick(query)}
              sx={{
                cursor: 'pointer',
                borderColor: theme.palette.ai.main,
                color: theme.palette.ai.dark,
                fontWeight: 500,
                height: 24,
                fontSize: '0.75rem',
                '&:hover': {
                  backgroundColor: `${theme.palette.ai.light}40`,
                  borderColor: theme.palette.ai.dark,
                },
              }}
              variant='outlined'
            />
          ))}
        </Box>
      )}

      {/* Disclaimer */}
      <Typography variant='caption' sx={{ textAlign: 'center', color: theme.palette.grey[500], px: 2, pb: 0.5, fontSize: '0.65rem' }}>
        Stethi is AI-powered and can make mistakes.
      </Typography>

      {/* Job Context Chip */}
      {activeJobContext && (
        <Box sx={{ px: 2, pb: 0.5 }}>
          <Chip
            label={`Asking about: ${activeJobContext.title || 'Job'}`}
            size="small"
            onDelete={handleDismissJobContext}
            deleteIcon={<CloseIcon sx={{ fontSize: '0.9rem !important' }} />}
            sx={{
              backgroundColor: `${theme.palette.ai.light}30`,
              borderColor: theme.palette.ai.main,
              color: theme.palette.ai.dark,
              fontWeight: 500,
              fontSize: '0.75rem',
              maxWidth: '100%',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
            variant="outlined"
          />
        </Box>
      )}

      {/* Input Area */}
      <Box
        component='form'
        onSubmit={handleSubmit}
        sx={{
          p: 1.5,
          borderTop: 1,
          borderColor: theme.palette.grey[300],
          backgroundColor: theme.palette.background.paper,
          position: 'relative',
          paddingBottom: isMobile ? 'max(12px, env(safe-area-inset-bottom))' : 1.5,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            backgroundColor: isAiTyping ? theme.palette.ai.main : 'transparent',
            transition: 'background-color 0.3s ease',
          },
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            variant='outlined'
            placeholder="Ask Stethi..."
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isAiTyping}
            inputRef={inputRef}
            size="small"
            sx={{
              fontFamily: '"Kumbh Sans", sans-serif',
              '& .MuiOutlinedInput-root': {
                fontFamily: '"Kumbh Sans", sans-serif',
                fontSize: { xs: '16px', md: '0.875rem' },
                '& textarea': {
                  fontFamily: '"Kumbh Sans", sans-serif',
                  fontSize: { xs: '16px', md: '0.875rem' },
                },
                '& fieldset': {
                  borderColor: isAiTyping ? theme.palette.ai.main : 'rgba(0, 0, 0, 0.23)',
                  borderWidth: isAiTyping ? '2px' : '1px',
                },
                '&:hover fieldset': {
                  borderColor: theme.palette.ai.main,
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.palette.ai.main,
                },
              },
            }}
          />
          {isAiTyping ? (
            <Button
              variant='contained'
              onClick={handleStop}
              sx={{
                minWidth: '40px',
                height: '40px',
                alignSelf: 'flex-end',
                backgroundColor: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.dark,
                },
              }}
            >
              <StopIcon sx={{ fontSize: '1.1rem' }} />
            </Button>
          ) : (
            <Button
              type='submit'
              variant='contained'
              disabled={!prompt.trim()}
              sx={{
                minWidth: '40px',
                height: '40px',
                alignSelf: 'flex-end',
                backgroundColor: theme.palette.ai.main,
                '&:hover': {
                  backgroundColor: theme.palette.ai.dark,
                },
              }}
            >
              <SendIcon sx={{ fontSize: '1.1rem' }} />
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )

  // Resize handle element
  const resizeHandle = (
    <Box
      onMouseDown={startResize}
      sx={{
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: '6px',
        cursor: 'col-resize',
        backgroundColor: 'transparent',
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '1px',
          height: '100%',
          backgroundColor: theme.palette.divider,
        },
        '&::after': {
          content: '""',
          width: '3px',
          height: '32px',
          borderRadius: '2px',
          backgroundColor: theme.palette.grey[300],
          zIndex: 1,
          transition: 'background-color 0.15s ease',
        },
        '&:hover': {
          '&::before': { backgroundColor: theme.palette.primary.main },
          '&::after': { backgroundColor: theme.palette.primary.main },
        },
        ...(isResizing && {
          '&::before': { backgroundColor: theme.palette.primary.main },
          '&::after': { backgroundColor: theme.palette.primary.main },
        }),
      }}
    />
  )

  // === INLINE MODE ===
  if (inline) {
    // Collapsed state: thin vertical tab
    if (collapsed) {
      return (
        <Box
          onClick={onCollapse}
          sx={{
            width: 40,
            flexShrink: 0,
            height: '100%',
            borderLeft: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: `${theme.palette.ai.light}20`,
            },
          }}
        >
          <MyStethiAIIcon sx={{ color: theme.palette.ai.main, fontSize: '1.2rem', mb: 1 }} />
          <Typography
            sx={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              fontSize: '0.75rem',
              fontWeight: 600,
              color: theme.palette.ai.dark,
              letterSpacing: '0.05em',
            }}
          >
            Stethi
          </Typography>
        </Box>
      )
    }

    // Expanded inline sidebar
    return (
      <Box
        sx={{
          width: sidebarWidth,
          flexShrink: 0,
          height: '100%',
          borderLeft: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
        }}
      >
        {resizeHandle}
        {chatContent}
      </Box>
    )
  }

  // === DRAWER MODE (mobile + legacy) ===
  return (
    <>
      {/* Stethi Toggle Button */}
      {showFab && (
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: isDrawerOpen ? (isMobile ? 16 : sidebarWidth + 24) : { xs: 16, md: 24 },
            zIndex: 9999,
            transition: 'right 0.3s ease',
          }}
        >
        {isDrawerOpen ? (
          <Fab
            aria-label="collapse Stethi"
            onClick={toggleDrawer}
            size="medium"
            sx={{
              backgroundColor: theme.palette.ai.main,
              width: 56,
              height: 56,
              minHeight: 44,
              minWidth: 44,
              '&:hover': {
                backgroundColor: theme.palette.ai.dark,
              },
              boxShadow: theme.palette.ai.shadow,
            }}
          >
            <ChevronRightIcon sx={{ color: theme.palette.common.white, fontSize: '1.5rem' }} />
          </Fab>
        ) : (
          <Box
            role="button"
            tabIndex={0}
            aria-label="open Stethi"
            onClick={toggleDrawer}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDrawer() } }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              background: `linear-gradient(135deg, ${theme.palette.ai.dark} 0%, ${theme.palette.ai.main} 100%)`,
              borderRadius: '12px',
              padding: { xs: '10px 14px', md: '12px 16px' },
              boxShadow: theme.palette.ai.shadow,
              cursor: 'pointer',
              '&:hover': {
                boxShadow: theme.palette.shadows.heavy,
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <MyStethiAIIcon sx={{ color: theme.palette.common.white, fontSize: '1.3rem', mr: 1 }} />
            <Typography
              variant="button"
              sx={{
                color: theme.palette.common.white,
                fontWeight: 600,
                fontSize: '0.85rem',
                textTransform: 'none',
              }}
            >
              Stethi
            </Typography>
          </Box>
        )}
      </Box>
      )}

      {/* Stethi Drawer */}
      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        variant="persistent"
        open={isDrawerOpen}
        className="ai-drawer-container"
        transitionDuration={{
          enter: transitions.enteringScreen,
          exit: transitions.leavingScreen,
        }}
        PaperProps={{
          sx: isMobile ? {
            width: '100%',
            height: mobileDrawerState === 'expanded' ? '70vh' : '100vh',
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            zIndex: 1300,
            ...springTransition('height', transitions.standard),
          } : {
            width: { xs: '100%', sm: 400, md: sidebarWidth },
            maxWidth: '100vw',
            height: '100vh',
            zIndex: 1200,
          }
        }}
      >
        {/* Resize Handle - Desktop only */}
        {!isMobile && resizeHandle}
        {chatContent}
      </Drawer>
    </>
  )
}

export default AIChatBot
